import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingCart, Plus, Clock, ChevronDown, ChevronRight, CheckCircle2,
} from "lucide-react";
import api from "../../lib/api";

/**
 * PortalOrderSummaryBlock — compact, collapsible summary that lives in the
 * main portal view. Shows the latest 3 orders + a big "Place an order"
 * button that opens the full-page storefront (/portal/order-new).
 *
 * `isPreview` — when staff views the portal, the storefront route is
 * gated by `ClientOnly` in App.js, so we disable the CTA here too rather
 * than bouncing staff to the CRM dashboard.
 */
export default function PortalOrderSummaryBlock({ customerId, defaultOpen = false, isPreview = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!open || !customerId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/order-requests?customer_id=${customerId}`);
        setOrders((data || []).slice(0, 5));
      } finally { setLoading(false); }
    })();
  }, [open, customerId]);

  const pendingCount = orders.filter((o) => o.status === "pending_review").length;

  return (
    <section className="hv-card p-4" data-testid="portal-order-summary">
      <header
        className="flex items-center justify-between cursor-pointer gap-2"
        onClick={() => setOpen((v) => !v)}
        data-testid="portal-order-summary-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-[#647087]" /> : <ChevronRight className="w-4 h-4 text-[#647087]" />}
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] shrink-0">
            <ShoppingCart className="w-4 h-4" />
          </div>
          <h3 className="font-display text-base font-bold truncate">Orders</h3>
          {pendingCount > 0 && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-amber-400/30 text-amber-300 bg-amber-400/10">
              {pendingCount} pending
            </span>
          )}
        </div>
        <Link
          to={isPreview ? `/portal/order-new?customer_id=${customerId}&as_staff=1` : "/portal/order-new"}
          onClick={(e) => e.stopPropagation()}
          className="hv-btn hv-btn-ghost text-xs shrink-0"
          style={{ borderColor: "#1FB6C1", color: "#1FB6C1" }}
          data-testid="portal-order-summary-cta"
        ><Plus className="w-3 h-3" /> Place an order</Link>
      </header>

      {open && (
        <div className="mt-3 space-y-2" data-testid="portal-order-summary-panel">
          {loading ? (
            <div className="text-xs text-[#647087]">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="text-xs text-[#647087] italic py-2">
              You haven't placed any orders yet. Tap <strong>Place an order</strong> above to open the storefront.
            </div>
          ) : (
            orders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 text-xs border border-white/5 rounded-md px-3 py-2 bg-white/[0.02]">
                <div className="w-8 h-8 rounded-md flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                  {o.status === "pending_review" ? <Clock className="w-3 h-3 text-amber-300" /> : <CheckCircle2 className="w-3 h-3 text-emerald-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {o.lines?.length || 0} item{o.lines?.length === 1 ? "" : "s"}
                    <span className="text-[#647087] ml-2 uppercase tracking-wider text-[10px]">{o.status?.replace("_", " ")}</span>
                  </div>
                  <div className="text-[10px] text-[#647087] truncate">
                    {o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""}
                    {o.notes ? ` · ${o.notes.slice(0, 60)}` : ""}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  );
}
